/* eslint-disable max-lines */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useModalEscClose } from '@utils/hooks/use-modal-esc';
import { useThemeContext } from '@utils/hooks/use-theme-context';
import { X, Layers, Zap, AlignLeft, Languages, Loader2 } from 'lucide-react';
import { AdminPlan, AdminPlanPayload, AdminPlanType } from '@utils/types/admin';
import { useCreateAdminPlan } from '@utils/api/admin/use-create-admin-plan';
import { useUpdateAdminPlan } from '@utils/api/admin/use-update-admin-plan';
import toast from 'react-hot-toast';

type AdminPlanFormModalProps = {
  plan: AdminPlan | null;
  onClose: () => void;
};

const FEATURES = [
  { key: 'batch_enabled', icon: Layers, label: 'batch-processing' },
  { key: 'realtime_enabled', icon: Zap, label: 'realtime-processing' },
  { key: 'alignment_enabled', icon: AlignLeft, label: 'alignment' },
  { key: 'translation_enabled', icon: Languages, label: 'translation' }
] as const;

/**
 * Modal for creating or editing an on-prem plan
 */
export default function AdminPlanFormModal({
  plan,
  onClose
}: AdminPlanFormModalProps) {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();
  const isEditing = !!plan;

  const createMutation = useCreateAdminPlan();
  const updateMutation = useUpdateAdminPlan();

  // Form state
  const [sku, setSku] = useState(plan?.sku ?? '');
  const [description, setDescription] = useState(plan?.description ?? '');
  const [type, setType] = useState<AdminPlanType>(plan?.type ?? 'perpetual');
  const [sttLimit, setSttLimit] = useState<string>(
    plan?.stt_limit !== null && plan?.stt_limit !== undefined
      ? String(plan.stt_limit)
      : ''
  );
  const [batchEnabled, setBatchEnabled] = useState(plan?.batch_enabled ?? true);
  const [realtimeEnabled, setRealtimeEnabled] = useState(
    plan?.realtime_enabled ?? true
  );
  const [maxRealtimeStreams, setMaxRealtimeStreams] = useState<string>(
    plan?.realtime_max_concurrent_streams != null
      ? String(plan.realtime_max_concurrent_streams)
      : ''
  );
  const [realtimeLanguagesCsv, setRealtimeLanguagesCsv] = useState<string>(
    plan?.realtime_languages?.length ? plan.realtime_languages.join(', ') : ''
  );
  const [alignmentEnabled, setAlignmentEnabled] = useState(
    plan?.alignment_enabled ?? true
  );
  const [translationEnabled, setTranslationEnabled] = useState(
    plan?.translation_enabled ?? true
  );
  const [isFairUse, setIsFairUse] = useState(plan?.is_fair_use ?? false);
  const [fairUseRateLimit, setFairUseRateLimit] = useState<string>(
    plan?.fair_use_rate_limit !== null &&
      plan?.fair_use_rate_limit !== undefined
      ? String(plan.fair_use_rate_limit)
      : ''
  );
  const [customModelYearlyLimit, setCustomModelYearlyLimit] = useState<string>(
    plan?.custom_model_yearly_limit !== null &&
      plan?.custom_model_yearly_limit !== undefined
      ? String(plan.custom_model_yearly_limit)
      : ''
  );

  // Validation
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!sku.trim()) {
      newErrors.sku = t('sku-is-required');
    }
    if (!description.trim()) {
      newErrors.description = t('description-is-required');
    }
    if (sttLimit && isNaN(Number(sttLimit))) {
      newErrors.sttLimit = t('invalid-number');
    }
    if (isFairUse && fairUseRateLimit && isNaN(Number(fairUseRateLimit))) {
      newErrors.fairUseRateLimit = t('invalid-number');
    }
    if (customModelYearlyLimit && isNaN(Number(customModelYearlyLimit))) {
      newErrors.customModelYearlyLimit = t('invalid-number');
    }
    if (
      realtimeEnabled &&
      maxRealtimeStreams &&
      (isNaN(Number(maxRealtimeStreams)) || Number(maxRealtimeStreams) < 0)
    ) {
      newErrors.maxRealtimeStreams = t('invalid-number');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;

    const parsedLanguages = realtimeEnabled
      ? Array.from(
          new Set(
            realtimeLanguagesCsv
              .split(',')
              .map(s => s.trim().toLowerCase())
              .filter(Boolean)
          )
        )
      : [];

    const payload: AdminPlanPayload = {
      sku: sku.trim(),
      description: description.trim(),
      type,
      stt_limit: sttLimit ? Number(sttLimit) : null,
      batch_enabled: batchEnabled,
      realtime_enabled: realtimeEnabled,
      realtime_max_concurrent_streams:
        realtimeEnabled && maxRealtimeStreams
          ? Number(maxRealtimeStreams)
          : null,
      realtime_languages:
        realtimeEnabled && parsedLanguages.length ? parsedLanguages : null,
      alignment_enabled: alignmentEnabled,
      translation_enabled: translationEnabled,
      is_fair_use: isFairUse,
      fair_use_rate_limit:
        isFairUse && fairUseRateLimit ? Number(fairUseRateLimit) : null,
      custom_model_yearly_limit: customModelYearlyLimit
        ? Number(customModelYearlyLimit)
        : null
    };

    const onError = (error: unknown) => {
      const err = error as {
        response?: {
          data?: {
            error_description?: string;
            detail?: string;
            message?: string;
          };
        };
      };
      const msg =
        err?.response?.data?.error_description ||
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        t('an-error-occurred');
      toast.error(msg);
    };

    if (isEditing && plan) {
      updateMutation.mutate(
        { id: plan.id, payload },
        { onSuccess: onClose, onError }
      );
    } else {
      createMutation.mutate(payload, { onSuccess: onClose, onError });
    }
  };

  const isLoading = createMutation.isLoading || updateMutation.isLoading;

  // Close on escape key (shared top-most-modal handling)
  useModalEscClose(true, onClose);

  const featureStates = {
    batch_enabled: { value: batchEnabled, setter: setBatchEnabled },
    realtime_enabled: { value: realtimeEnabled, setter: setRealtimeEnabled },
    alignment_enabled: { value: alignmentEnabled, setter: setAlignmentEnabled },
    translation_enabled: {
      value: translationEnabled,
      setter: setTranslationEnabled
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* Modal */}
      <div
        className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl shadow-xl"
        style={{ backgroundColor: getColour('baseLayer') }}
      >
        {/* Header */}
        <div
          className="sticky top-0 flex items-center justify-between p-4 border-b z-10"
          style={{
            borderColor: getColour('border'),
            backgroundColor: getColour('baseLayer')
          }}
        >
          <h2
            className="text-lg font-semibold"
            style={{ color: getColour('normalText') }}
          >
            {isEditing ? t('edit-plan') : t('create-plan')}
          </h2>
          <button
            onClick={onClose}
            className="p-1 rounded-2xl hover:bg-opacity-80"
            style={{ backgroundColor: getColour('thirdLayer') }}
          >
            <X className="h-5 w-5" style={{ color: getColour('normalText') }} />
          </button>
        </div>

        {/* Form */}
        <div className="p-6 space-y-6">
          {/* Basic Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* SKU */}
            <div>
              <label
                className="block text-xs font-medium uppercase tracking-wide mb-2"
                style={{ color: getColour('inactiveText') }}
              >
                {t('sku')} *
              </label>
              <input
                type="text"
                value={sku}
                onChange={e => setSku(e.target.value)}
                disabled={isEditing}
                className={`w-full px-3 py-2 rounded-2xl text-sm border disabled:opacity-50 ${errors.sku ? 'border-red-500 dark:border-red-400' : 'border-hairline'}`}
                style={{
                  backgroundColor: getColour('thirdLayer'),
                  color: getColour('normalText')
                }}
                placeholder="e.g., on-prem-unlimited"
              />
              {errors.sku && (
                <p className="text-xs mt-1 text-red-500">{errors.sku}</p>
              )}
            </div>

            {/* Type */}
            <div>
              <label
                className="block text-xs font-medium uppercase tracking-wide mb-2"
                style={{ color: getColour('inactiveText') }}
              >
                {t('type')} *
              </label>
              <select
                value={type}
                onChange={e => setType(e.target.value as AdminPlanType)}
                className="w-full px-3 py-2 rounded-2xl text-sm"
                style={{
                  backgroundColor: getColour('thirdLayer'),
                  border: `1px solid ${getColour('border')}`,
                  color: getColour('normalText')
                }}
              >
                <option value="perpetual">{t('perpetual')}</option>
                <option value="bundle">{t('bundle')}</option>
              </select>
            </div>
          </div>

          {/* Description */}
          <div>
            <label
              className="block text-xs font-medium uppercase tracking-wide mb-2"
              style={{ color: getColour('inactiveText') }}
            >
              {t('description')} *
            </label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              rows={2}
              className={`w-full px-3 py-2 rounded-2xl text-sm resize-none border ${errors.description ? 'border-red-500 dark:border-red-400' : 'border-hairline'}`}
              style={{
                backgroundColor: getColour('thirdLayer'),
                color: getColour('normalText')
              }}
              placeholder={t('enter-plan-description')}
            />
            {errors.description && (
              <p className="text-xs mt-1 text-red-500">{errors.description}</p>
            )}
          </div>

          {/* STT Limit */}
          <div>
            <label
              className="block text-xs font-medium uppercase tracking-wide mb-2"
              style={{ color: getColour('inactiveText') }}
            >
              {t('stt-limit')} ({t('minutes')})
            </label>
            <input
              type="number"
              value={sttLimit}
              onChange={e => setSttLimit(e.target.value)}
              min={0}
              className={`w-full px-3 py-2 rounded-2xl text-sm border ${errors.sttLimit ? 'border-red-500 dark:border-red-400' : 'border-hairline'}`}
              style={{
                backgroundColor: getColour('thirdLayer'),
                color: getColour('normalText')
              }}
              placeholder={t('leave-empty-for-unlimited')}
            />
            {errors.sttLimit && (
              <p className="text-xs mt-1 text-red-500">{errors.sttLimit}</p>
            )}
            <p
              className="text-xs mt-1"
              style={{ color: getColour('inactiveText') }}
            >
              {t('stt-limit-description')}
            </p>
          </div>

          {/* Custom model yearly limit */}
          <div>
            <label
              className="block text-xs font-medium uppercase tracking-wide mb-2"
              style={{ color: getColour('inactiveText') }}
            >
              {t('custom-models-per-year')}
            </label>
            <input
              type="number"
              value={customModelYearlyLimit}
              onChange={e => setCustomModelYearlyLimit(e.target.value)}
              min={0}
              className={`w-full px-3 py-2 rounded-2xl text-sm border ${errors.customModelYearlyLimit ? 'border-red-500 dark:border-red-400' : 'border-hairline'}`}
              style={{
                backgroundColor: getColour('thirdLayer'),
                color: getColour('normalText')
              }}
              placeholder={t('leave-empty-for-unlimited')}
            />
            {errors.customModelYearlyLimit && (
              <p className="text-xs mt-1 text-red-500">
                {errors.customModelYearlyLimit}
              </p>
            )}
            <p
              className="text-xs mt-1"
              style={{ color: getColour('inactiveText') }}
            >
              {t('custom-models-per-year-description')}
            </p>
          </div>

          {/* Feature Toggles */}
          <div>
            <label
              className="block text-xs font-medium uppercase tracking-wide mb-3"
              style={{ color: getColour('inactiveText') }}
            >
              {t('enabled-features')}
            </label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {FEATURES.map(({ key, icon: Icon, label }) => {
                const { value, setter } = featureStates[key];
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setter(!value)}
                    className={`flex items-center gap-2 p-3 rounded-2xl border transition-colors ${value ? 'border-green-500 bg-green-500/10' : 'border-hairline'}`}
                    style={
                      value
                        ? undefined
                        : { backgroundColor: getColour('thirdLayer') }
                    }
                  >
                    <Icon
                      className={`h-4 w-4 ${value ? 'text-green-600 dark:text-green-400' : ''}`}
                      style={
                        value ? undefined : { color: getColour('inactiveText') }
                      }
                    />
                    <span
                      // eslint-disable-next-line no-restricted-syntax
                      className={`text-xs font-medium ${value ? 'text-green-600 dark:text-green-400' : ''}`}
                      style={
                        value ? undefined : { color: getColour('normalText') }
                      }
                    >
                      {t(label)}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Realtime limits (only when realtime is enabled) */}
          {realtimeEnabled && (
            <div
              className="rounded-2xl border p-4 space-y-3"
              style={{
                borderColor: getColour('border'),
                backgroundColor: getColour('thirdLayer')
              }}
            >
              <label
                className="block text-xs font-medium uppercase tracking-wide"
                style={{ color: getColour('inactiveText') }}
              >
                {t('realtime-limits')}
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label
                    className="block text-xs font-medium mb-1"
                    style={{ color: getColour('inactiveText') }}
                  >
                    {t('max-concurrent-realtime-streams')}
                  </label>
                  <input
                    type="number"
                    min={0}
                    value={maxRealtimeStreams}
                    onChange={e => setMaxRealtimeStreams(e.target.value)}
                    className={`w-full px-3 py-2 rounded-2xl text-sm border ${errors.maxRealtimeStreams ? 'border-red-500 dark:border-red-400' : 'border-hairline'}`}
                    style={{
                      backgroundColor: getColour('secondLayer'),
                      color: getColour('normalText')
                    }}
                    placeholder={t('leave-empty-for-unlimited')}
                  />
                  {errors.maxRealtimeStreams && (
                    <p className="text-xs mt-1 text-red-500">
                      {errors.maxRealtimeStreams}
                    </p>
                  )}
                </div>
                <div>
                  <label
                    className="block text-xs font-medium mb-1"
                    style={{ color: getColour('inactiveText') }}
                  >
                    {t('allowed-realtime-languages')}
                  </label>
                  <input
                    type="text"
                    value={realtimeLanguagesCsv}
                    onChange={e => setRealtimeLanguagesCsv(e.target.value)}
                    className="w-full px-3 py-2 rounded-2xl text-sm"
                    style={{
                      backgroundColor: getColour('secondLayer'),
                      border: `1px solid ${getColour('border')}`,
                      color: getColour('normalText')
                    }}
                    placeholder={t('realtime-languages-placeholder')}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Fair Use */}
          <div
            className="rounded-2xl border p-4"
            style={{
              borderColor: getColour('border'),
              backgroundColor: getColour('thirdLayer')
            }}
          >
            <div className="flex items-center justify-between mb-3">
              <div>
                <label
                  className="text-sm font-medium"
                  style={{ color: getColour('normalText') }}
                >
                  {t('enable-fair-use')}
                </label>
                <p
                  className="text-xs"
                  style={{ color: getColour('inactiveText') }}
                >
                  {t('fair-use-description')}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsFairUse(!isFairUse)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${isFairUse ? 'bg-blue-500' : ''}`}
                style={
                  isFairUse
                    ? undefined
                    : { backgroundColor: getColour('border') }
                }
              >
                <span
                  // eslint-disable-next-line no-restricted-syntax
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    isFairUse ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            {isFairUse && (
              <div
                className="mt-3 pt-3 border-t"
                style={{ borderColor: getColour('border') }}
              >
                <label
                  className="block text-xs font-medium uppercase tracking-wide mb-2"
                  style={{ color: getColour('inactiveText') }}
                >
                  {t('rate-limit')} ({t('requests-per-minute')})
                </label>
                <input
                  type="number"
                  value={fairUseRateLimit}
                  onChange={e => setFairUseRateLimit(e.target.value)}
                  min={1}
                  className={`w-full px-3 py-2 rounded-2xl text-sm border ${errors.fairUseRateLimit ? 'border-red-500 dark:border-red-400' : 'border-hairline'}`}
                  style={{
                    backgroundColor: getColour('secondLayer'),
                    color: getColour('normalText')
                  }}
                  placeholder="e.g., 60"
                />
                {errors.fairUseRateLimit && (
                  <p className="text-xs mt-1 text-red-500">
                    {errors.fairUseRateLimit}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div
          className="sticky bottom-0 flex items-center justify-end gap-3 p-4 border-t"
          style={{
            borderColor: getColour('border'),
            backgroundColor: getColour('baseLayer')
          }}
        >
          <button
            onClick={onClose}
            disabled={isLoading}
            className="px-4 py-2 rounded-2xl text-sm font-medium"
            style={{
              backgroundColor: getColour('thirdLayer'),
              color: getColour('normalText')
            }}
          >
            {t('cancel')}
          </button>
          <button
            onClick={handleSubmit}
            disabled={isLoading}
            className="px-4 py-2 rounded-2xl text-sm font-medium flex items-center gap-2 bg-blue-500 text-white disabled:opacity-50"
          >
            {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
            {isEditing ? t('save-changes') : t('create-plan')}
          </button>
        </div>
      </div>
    </div>
  );
}
